import * as React from 'react';
import { Stage, Layer } from 'react-konva';
import CanvasImage from './CanvasImage';
import BoxArea from './BoxArea';
import { Question } from '@nexxt/common/types';
import {
    parseCoordinatesFromOriginal,
    parseCoordinatesToOriginal,
    isKonvaTouchEnabled,
    handleKonvaTouch,
    handleKonvaTouchEnd,
    zoomKonvaStage,
    orderByOverlap
} from '@nexxt/common/utils/question';
import { COLOR_THEME, KEY_NAME } from '@nexxt/common/constants';
import Modal, {
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalTitle
} from '../../../../common/modal';

interface ImageContainerProps {
    imageURL: string;
    question: Question;
    showLabels?: boolean;
    handleOptionFields: (
        keyName: string,
        value: any,
        deletedOptionIDs?: number[]
    ) => void;
}
interface ImageContainerState {
    component: React.Component;
    rectangles: any[];
    selectedShapeName: any;
    selectedShape: any;
    enableTextEdit: boolean;
    width: any;
    height: any;
    ratio: any;
    showModal: boolean;
    deletingId: string;
    currentHover: any;
    containerWidth: number;
}

class ImageContainer extends React.Component<
    ImageContainerProps,
    ImageContainerState
> {
    state = {
        component: this,
        rectangles: [],
        selectedShapeName: null,
        selectedShape: null,
        enableTextEdit: false,
        width: null,
        height: null,
        ratio: 1,
        showModal: false,
        deletingId: null,
        currentHover: null,
        containerWidth: 0
    };

    private stageRef;
    stickyFooter: HTMLDivElement;
    lastCenter = null;
    lastDist = 0;

    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.question.options !== prevState.rectangles) {
            return {
                ...prevState,
                rectangles: nextProps.question.options.length
                    ? prevState.component.formatBoxes(
                          nextProps.question.options
                      )
                    : []
            };
        } else return null;
    }

    componentDidMount(): void {
        this.resizeChart();
        window.addEventListener('resize', this.resizeChart);
    }

    componentWillUnmount(): void {
        window.removeEventListener('resize', this.resizeChart);
    }

    resizeChart = () => {
        const containerWidth = document.getElementById(
            `computer-vision--body--content`
        ).offsetWidth;

        this.setState({
            containerWidth
        });
    };

    formatBoxes = (boxes) => {
        const formattedBoxes = boxes.map((box, i) => {
            let coordinates = Object.assign({}, box.coordinates);
            let scaled = false;

            // check if the coordinates are relative to the original image
            // i.e. these are coordinates from the object annotation
            // check out computerVision function in data-handler.ts (portal-server)
            // for more details
            if (coordinates.x > 0 && coordinates.x < 1) {
                coordinates = {
                    x: Math.round(coordinates.x * this.state.width),
                    y: Math.round(coordinates.y * this.state.height),
                    width: Math.round(coordinates.width * this.state.width),
                    height: Math.round(coordinates.height * this.state.height)
                };
                scaled = true;
            }

            return {
                ...coordinates,
                id: box.id || null,
                text: box.label,
                key: { i },
                boxID: box.value,
                stroke: box.new ? '#e80303' : COLOR_THEME.blue,
                strokeWidth: box.new ? 2 : 1,
                scaled
            };
        });

        return parseCoordinatesFromOriginal(formattedBoxes, this.state.ratio);
    };

    getSize = (w, h, ratio) => {
        this.setState(
            (prevState) => ({
                ...prevState,
                width: w,
                height: h,
                ratio
            }),
            () =>
                this.setState({
                    rectangles: this.formatBoxes(this.props.question.options)
                })
        );
    };
    handleStageMouseDown = (e) => {
        // clicked on stage - cler selection
        if (e.target === e.target.getStage()) {
            this.setState({
                ...this.state,
                selectedShapeName: null,
                selectedShape: null
            });
            return;
        }
        // clicked on transformer - do nothing
        const clickedOnTransformer =
            e.target.getParent().className === 'Transformer';
        if (clickedOnTransformer) {
            return;
        }

        // find clicked rect by its name
        const name = e.target.name();
        const rect = this.state.rectangles.find(
            (r) => r.boxID === name.split('-')[0]
        );
        if (name) {
            this.setState({
                ...this.state,
                selectedShape: rect,
                selectedShapeName: name
            });
        } else {
            this.setState({
                ...this.state,
                selectedShape: null,
                selectedShapeName: null
            });
        }
    };
    handleText = (evt) => {
        const selected = this.state.selectedShape;
        selected.text = evt.target.value;
        const updateRect = this.state.rectangles;
        const currentIndex = updateRect.findIndex(
            (box) => box.boxID == selected.boxID
        );
        updateRect[currentIndex] = selected;

        const optionedRects = updateRect.map((rect, i) => {
            return {
                id: rect.id,
                value: rect.boxID,
                label: rect.text,
                coordinates: {
                    rotation: rect.rotation,
                    x: rect.x,
                    y: rect.y,
                    width: rect.width,
                    height: rect.height
                }
            };
        });

        this.props.handleOptionFields(
            'options',
            parseCoordinatesToOriginal(optionedRects, this.state.ratio)
        );
    };
    onChangeEditStatus = () => (status) => {
        this.setState({
            enableTextEdit: status
        });
    };

    handleTextareaKeyDown = (e) => {
        if (e.key === KEY_NAME.ENTER || e.key === KEY_NAME.ESCAPE) {
            this.setState({
                ...this.state,
                selectedShapeName: null,
                selectedShape: null
            });
        }
    };
    handleMove = (newRect, index) => {
        const { rectangles } = this.state;
        const rects = rectangles.slice();
        rects[index] = newRect;
        this.setState({
            rectangles: rects
        });

        const optionedRects = rects.map((rect, i) => {
            return {
                id: rect.id,
                value: rect.boxID,
                label: rect.text,
                coordinates: {
                    rotation: rect.rotation,
                    x: rect.x,
                    y: rect.y,
                    width: rect.width,
                    height: rect.height
                }
            };
        });
        this.props.handleOptionFields(
            'options',
            parseCoordinatesToOriginal(optionedRects, this.state.ratio)
        );
    };

    handleOptionDelete = (id) => {
        document.body.style.cursor = 'default';

        const { rectangles } = this.state;
        const index = rectangles.findIndex((rect) => rect.boxID === id);
        const deletedIDs = [];
        if (rectangles[index].id > 0) {
            deletedIDs.push(rectangles[index].id);
        }

        rectangles.splice(index, 1);
        const optionedRects = rectangles.map((rect, i) => {
            return {
                id: rect.id,
                value: String(i + 1),
                label: rect.text,
                coordinates: {
                    rotation: rect.rotation,
                    x: rect.x,
                    y: rect.y,
                    width: rect.width,
                    height: rect.height
                }
            };
        });
        this.props.handleOptionFields(
            'options',
            parseCoordinatesToOriginal(optionedRects, this.state.ratio),
            deletedIDs
        );
        this.toggleDeleteModal(null);
    };

    toggleDeleteModal = (id) => {
        this.setState({
            deletingId: id,
            selectedShape: id ? this.state.selectedShape : null,
            selectedShapeName: id ? this.state.selectedShapeName : null,
            showModal: !this.state.showModal
        });
    };

    /**
     * onMouseOver handler in Rect Konva component
     * @param event
     * @param area
     */
    handleMouseOverRect = (event, area?) => {
        event.target.getStage().container().style.cursor = 'pointer';

        this.setState({
            currentHover: area
        });
    };

    /**
     * onMouseOut handler in Rect Konva component
     * @param event
     * @param area
     */
    handleMouseOutRect = (event) => {
        event.target.getStage().container().style.cursor = 'default';
        this.setState({
            currentHover: null
        });
    };

    render() {
        const {
            width,
            height,
            rectangles,
            selectedShape,
            selectedShapeName,
            enableTextEdit
        } = this.state;

        const orderedRect = orderByOverlap(rectangles);

        const { imageURL } = this.props;
        return (
            <>
                <div
                    className="computer-vision--body--content"
                    id="computer-vision--body--content"
                >
                    {this.state.showModal && this.state.selectedShape && (
                        <Modal
                            className="modal__heightauto"
                            isOpen={this.state.showModal}
                        >
                            <ModalHeader
                                onClose={() => this.toggleDeleteModal(null)}
                            >
                                <ModalTitle>Confirmation</ModalTitle>
                            </ModalHeader>
                            <ModalBody>
                                {`Are you sure you would like to delete "${this.state.selectedShape.text}"?`}
                            </ModalBody>
                            <ModalFooter>
                                <button
                                    className="btn btn--sm btn--transparent m-r-10"
                                    onClick={() => this.toggleDeleteModal(null)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn--sm btn--orange"
                                    onClick={() =>
                                        this.handleOptionDelete(
                                            this.state.deletingId
                                        )
                                    }
                                >
                                    Delete
                                </button>
                            </ModalFooter>
                        </Modal>
                    )}
                    <Stage
                        ref={(node) => (this.stageRef = node)}
                        width={this.state.containerWidth}
                        height={height}
                        x={this.state.containerWidth / 2 - width / 2}
                        draggable={
                            !isKonvaTouchEnabled() &&
                            !enableTextEdit &&
                            !selectedShape
                        }
                        onMouseDown={this.handleStageMouseDown}
                        onTouchStart={this.handleStageMouseDown}
                        onTouchMove={(e) => {
                            handleKonvaTouch(
                                e,
                                this.stageRef,
                                this.lastCenter,
                                this.lastDist
                            );
                        }}
                        onTouchEnd={(e) => {
                            handleKonvaTouchEnd(
                                e,
                                this.lastCenter,
                                this.lastDist
                            );
                        }}
                        style={{
                            opacity: `${
                                selectedShape && enableTextEdit ? '.8' : '1'
                            }`
                        }}
                    >
                        <Layer>
                            <CanvasImage
                                src={imageURL}
                                getSize={this.getSize}
                            />
                            {orderedRect.map((rect, i) => {
                                const hide =
                                    selectedShapeName &&
                                    (!selectedShape
                                        ? false
                                        : selectedShape.boxID !== rect.boxID);
                                return (
                                    <BoxArea
                                        key={i}
                                        selectedShapeName={selectedShapeName}
                                        shapeProps={rect}
                                        isSelected={
                                            selectedShape &&
                                            rect.boxID === selectedShape.boxID
                                        }
                                        onChange={(newAttrs) => {
                                            this.handleMove(newAttrs, i);
                                        }}
                                        handleOptionDelete={
                                            this.toggleDeleteModal
                                        }
                                        changeEditStatus={this.onChangeEditStatus()}
                                        hide={hide}
                                        showLabel={this.props.showLabels}
                                    />
                                );
                            })}
                        </Layer>
                    </Stage>

                    {selectedShape && enableTextEdit && (
                        <div
                            className="edit-area--wrapper"
                            style={{
                                display: selectedShapeName ? 'block' : 'none',
                                top: selectedShape.y + 20 + 'px',
                                left:
                                    this.state.containerWidth / 2 -
                                    width / 2 +
                                    selectedShape.x +
                                    'px'
                            }}
                        >
                            <input
                                autoFocus
                                className="edit-area"
                                type="text"
                                style={{}}
                                value={selectedShape.text}
                                onChange={(e) => this.handleText(e)}
                                onKeyDown={(e) => this.handleTextareaKeyDown(e)}
                            />
                        </div>
                    )}
                </div>
                <div className="hotspot--image-buttons">
                    <div
                        onClick={(e) =>
                            zoomKonvaStage(e, 'zoom-in', this.stageRef)
                        }
                    >
                        +
                    </div>
                    <div
                        onClick={(e) =>
                            zoomKonvaStage(e, 'zoom-out', this.stageRef)
                        }
                    >
                        -
                    </div>
                </div>
            </>
        );
    }
}

export default ImageContainer;
